import { FC, useMemo } from 'react';

import { Loading } from '@shared/components/Loading';
import { Link } from 'react-router-dom';

import { Toast } from '../components/Toast';
import { TodayDeliveredTable } from '../components/TodayDeliveredTable';
import {
  todayKSTString,
  useDeliveredHistoryPage,
} from '../hooks/useDeliveredHistoryPage';

/** 'YYYY-MM-DD' → 'M월 D일' */
const formatDateLabel = (date: string) => {
  const [, m, d] = date.split('-');
  if (!m || !d) return date;
  return `${Number(m)}월 ${Number(d)}일`;
};

export const GiftDeliveredHistoryPage: FC = function GiftDeliveredHistoryPage() {
  const {
    date,
    setDate,
    isToday,
    isValidDate,
    shiftDate,
    goToday,
    status,
    delivered,
    showTable,
    setShowTable,
    handleBulkUpdateDelivered,
    toastMessage,
    showToast,
    toastDurationMs,
    copyToClipboard,
  } = useDeliveredHistoryPage();

  const totalWholeSum = useMemo(
    () => delivered.reduce((sum, c) => sum + (c.gifticon?.priceWhole ?? 0), 0),
    [delivered],
  );

  const isLoading = status === 'loading' || status === 'idle';
  const tableTitle = isToday ? '오늘 발송 완료' : `${formatDateLabel(date)} 발송 완료`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">발송내역 조회</h1>
            <p className="mt-1 text-sm text-gray-500">
              날짜를 선택해 해당 날짜에 발송 완료된 쿠폰의 도매가/매체를 확인·수정하세요.
            </p>
          </div>
          <Link
            to="/gift-seller/coupon-redemption"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            발송 대기 목록
          </Link>
        </div>
      </div>

      {/* 날짜 선택 */}
      <div className="flex flex-wrap items-center gap-2 px-4 pb-2 pt-4">
        <button
          onClick={() => shiftDate(-1)}
          disabled={!isValidDate}
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          ← 이전날
        </button>
        <input
          type="date"
          value={date}
          max={todayKSTString()}
          onChange={(e) => setDate(e.target.value)}
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
        />
        <button
          onClick={() => shiftDate(1)}
          disabled={!isValidDate || isToday}
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          다음날 →
        </button>
        <button
          onClick={goToday}
          disabled={isToday}
          className="rounded border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
        >
          오늘
        </button>
        {!isLoading && delivered.length > 0 && (
          <span className="ml-2 text-sm text-gray-600">
            총 <span className="font-semibold">{delivered.length}건</span> · 도매가 합산{' '}
            <span className="font-semibold text-indigo-600">
              {totalWholeSum.toLocaleString()}원
            </span>
          </span>
        )}
      </div>

      {/* 결과 */}
      {!isValidDate ? (
        <div className="px-4 py-12 text-center text-sm text-gray-500">
          조회할 날짜를 선택해주세요.
        </div>
      ) : isLoading ? (
        <div className="center-box py-20">
          <Loading />
        </div>
      ) : status === 'error' ? (
        <div className="px-4 py-12 text-center text-sm text-red-500">
          발송내역을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.
        </div>
      ) : delivered.length === 0 ? (
        <div className="px-4 py-12 text-center text-sm text-gray-500">
          {formatDateLabel(date)}에 발송 완료된 내역이 없습니다.
        </div>
      ) : (
        <TodayDeliveredTable
          todayDelivered={delivered}
          showTodayDelivered={showTable}
          setShowTodayDelivered={setShowTable}
          copyToClipboard={copyToClipboard}
          showToast={showToast}
          onBulkUpdate={handleBulkUpdateDelivered}
          title={tableTitle}
        />
      )}

      {/* Toast */}
      <Toast message={toastMessage} durationMs={toastDurationMs} />
    </div>
  );
};
